import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, doc, query, where, orderBy, onSnapshot, addDoc, setDoc, serverTimestamp, getDocs, getDoc, limit } from 'firebase/firestore';
import './AdminDashboard.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AnalyticsTab from './administrator/tabs/AnalyticsTab/AnalyticsTab';
import ChatsTab from './administrator/tabs/ChatsTab/ChatsTab';
import UsersTab from './administrator/tabs/UsersTab/UsersTab';
import NewUsersTab from './administrator/tabs/NewUsersTab/NewUsersTab';
import VendorCustomBuilds from './administrator/tabs/CustomTab/VendorCustomBuilds';
import PoliceTab from './administrator/tabs/PoliceTab/PoliceTab';
import VideoTab from './administrator/tabs/VideoTab/VideoTab';
import UserLookupTab from './administrator/tabs/UserLookupTab/UserLookupTab';

const AdminDashboard = () => {
    const [chats, setChats] = useState([]);
    const [proUsers, setProUsers] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('chats'); // 'chats', 'users', 'new_users', 'analytics', 'vendor_builds', or 'police'
    const messagesEndRef = useRef(null);
    const chatMessagesRef = useRef(null);
    
    // Analytics state
    const [analyticsData, setAnalyticsData] = useState([]);
    const [dateRange, setDateRange] = useState('week'); // 'week', 'month', 'year'
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    
    // Add this state for tracking backfill status
    const [backfillStatus, setBackfillStatus] = useState({ loading: false, message: '' });
    
    const [usersWithoutChats, setUsersWithoutChats] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Tab configuration
    const tabs = [
        { id: 'chats', label: 'Chats' },
        { id: 'users', label: 'Pro Users' },
        { id: 'new_users', label: `New Pro Users ${usersWithoutChats.length > 0 ? `(${usersWithoutChats.length})` : ''}` },
        { id: 'analytics', label: 'Analytics' },
        { id: 'vendor_builds', label: 'Vendor Builds' },
        { id: 'police', label: 'Police' },
        { id: 'videos', label: 'Videos' },
        { id: 'user_lookup', label: 'User Lookup' }
    ];

    const getCurrentTabLabel = () => {
        const currentTab = tabs.find(tab => tab.id === activeTab);
        return currentTab ? currentTab.label : 'Select Tab';
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setIsDropdownOpen(false);
    };

    // Remove the click-outside handler to avoid conflicts with Header dropdown

    const handleChatSelection = (chat) => {
        setSelectedChat(chat);
        
        // When admin selects a chat, update a field to indicate admin is viewing
        try {
            setDoc(doc(db, 'pro_chats', chat.id), {
                adminViewing: true,
                lastAdminAccess: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error("Error updating admin viewing status:", error);
        }
    };

    const handleCloseChat = () => {
        // Clear the selected chat
        setSelectedChat(null);
        // Clear messages
        setMessages([]);
        // Reset new message input
        setNewMessage('');
    };

    // Helper function to check if a chat has any messages
    const checkForMessages = async (chatId) => {
        const messagesRef = collection(db, 'pro_chats', chatId, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        const hasMessages = !messagesSnapshot.empty;
        return hasMessages;
    };

    // Updated helper function to identify users without chats or with empty chats
    const updateUsersWithoutChats = async (users, chatsList) => {
        try {
            // Create a map to store users with active chats
            const usersWithActiveChats = new Set();
            
            // Check each chat for messages
            for (const chat of chatsList) {
                const hasMessages = await checkForMessages(chat.id);
                if (hasMessages) {
                    usersWithActiveChats.add(chat.userId || chat.id);
                }
            }
            
            // Filter out users who have chats with messages
            const newUsers = users.filter(user => {
                const shouldInclude = !usersWithActiveChats.has(user.id);
                return shouldInclude;
            }).sort((a, b) => {
                const aTime = a.subscriptionStartDate?.toDate?.() || new Date();
                const bTime = b.subscriptionStartDate?.toDate?.() || new Date();
                return bTime - aTime;
            });

            setUsersWithoutChats(newUsers);
            
        } catch (error) {
            console.error("Error checking for messages:", error);
        }
    };

    // Update useEffect to handle async updateUsersWithoutChats
    useEffect(() => {
        const fetchProUsers = async () => {
            try {
                const usersRef = collection(db, 'users');
                
                // Query for users with pro_user role OR legacy isPro flag (for backward compatibility)
                const q = query(usersRef, 
                    where('role', '==', 'pro_user')
                );
                const querySnapshot = await getDocs(q);
                
                // Also check for legacy isPro users if needed
                const legacyQuery = query(usersRef, where('isPro', '==', true));
                const legacySnapshot = await getDocs(legacyQuery);
                
                const userMap = {};
                
                // Process role-based users
                querySnapshot.docs.forEach(doc => {
                    const userData = doc.data();
                    let displayName = userData.userProfile?.username || 
                                    userData.userProfile?.userEmail || 
                                    userData.email || 
                                    'Anonymous User';
                    
                    userMap[doc.id] = {
                        id: doc.id,
                        ...userData,
                        displayName
                    };
                });
                
                // Process legacy users (avoid duplicates)
                legacySnapshot.docs.forEach(doc => {
                    if (!userMap[doc.id]) {
                        const userData = doc.data();
                        let displayName = userData.userProfile?.username || 
                                        userData.userProfile?.userEmail || 
                                        userData.email || 
                                        'Anonymous User';
                        
                        userMap[doc.id] = {
                            id: doc.id,
                            ...userData,
                            displayName
                        };
                    }
                });
                
                const proUsersList = Object.values(userMap);
                setProUsers(proUsersList);
                await updateUsersWithoutChats(proUsersList, chats);
                
            } catch (error) {
                console.error("Error in fetchProUsers:", error);
            }
        };
        
        fetchProUsers();
    }, [chats]); // Dependency on chats ensures the list updates when new chats are created

    // Fetch all pro chats
    useEffect(() => {
        const chatsRef = collection(db, 'pro_chats');
        const q = query(chatsRef, orderBy('lastMessageTime', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                lastMessageTime: doc.data().lastMessageTime?.toDate() || new Date(),
                userName: doc.data().userName || 'Anonymous User'
            }));
            // Unread chats first, then by most recent message
            chatList.sort((a, b) => {
                const aUnread = (a.unreadByAdmin || 0) > 0 ? 1 : 0;
                const bUnread = (b.unreadByAdmin || 0) > 0 ? 1 : 0;
                if (bUnread !== aUnread) return bUnread - aUnread;
                return b.lastMessageTime - a.lastMessageTime;
            });
            setChats(chatList);
            setLoading(false);
        });
        
        return () => unsubscribe();
    }, []);
    
    // Fetch messages for selected chat
    useEffect(() => {
        if (!selectedChat) return;
        
        const messagesRef = collection(db, 'pro_chats', selectedChat.id, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messageList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate() || new Date()
            }));
            setMessages(messageList);
            
            // Mark messages as read
            snapshot.docChanges().forEach(change => {
                if (change.type === 'added' && 
                    change.doc.data().senderId === selectedChat.id && 
                    !change.doc.data().read) {
                    // Update the message as read
                    setDoc(doc(db, 'pro_chats', selectedChat.id, 'messages', change.doc.id), 
                        { read: true }, 
                        { merge: true }
                    );
                }
            });
            
            // Update unreadByAdmin count to 0
            setDoc(doc(db, 'pro_chats', selectedChat.id), 
                { unreadByAdmin: 0 }, 
                { merge: true }
            );
        });
        
        return () => unsubscribe();
    }, [selectedChat]);
    
    // Auto-scroll to bottom when new messages arrive — scroll the container
    // directly so the page itself never jumps
    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages]);
    
    const handleInitiateChat = async (user) => {
        // Check if a chat already exists for this user
        const existingChat = chats.find(chat => chat.id === user.id);
        
        if (existingChat) {
            // If chat exists, select it
            setSelectedChat(existingChat);
            setActiveTab('chats');
        } else {
            // Create a new chat document
            try {
                await setDoc(doc(db, 'pro_chats', user.id), {
                    userName: user.displayName || 'Anonymous User',
                    lastMessageTime: serverTimestamp(),
                    unreadByUser: 0,
                    unreadByAdmin: 0,
                    adminViewing: true,
                    lastAdminAccess: serverTimestamp()
                });
                
                // Create a new chat object and select it
                const newChat = {
                    id: user.id,
                    userName: user.displayName || 'Anonymous User',
                    lastMessageTime: new Date(),
                    unreadByUser: 0,
                    unreadByAdmin: 0
                };
                
                setSelectedChat(newChat);
                setActiveTab('chats');
            } catch (error) {
                console.error("Error initiating chat:", error);
            }
        }
    };
    
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedChat || isSending) return;
        
        setIsSending(true);
        
        try {
            // Add the new message
            const messageData = {
                text: newMessage,
                senderId: 'admin',
                timestamp: serverTimestamp(),
                read: false
            };
            
            try {
                await addDoc(collection(db, 'pro_chats', selectedChat.id, 'messages'), messageData);
            } catch (error) {
                console.error("Error adding admin message:", error);
            }
            
            // Update the chat document
            try {
                await setDoc(doc(db, 'pro_chats', selectedChat.id), {
                    lastMessageTime: serverTimestamp(),
                    unreadByUser: (messages.filter(m => m.senderId === 'admin' && !m.read).length + 1)
                }, { merge: true });
            } catch (error) {
                console.error("Error updating chat document:", error);
            }
            
            setNewMessage('');
        } catch (error) {
            console.error('Error sending admin message:', error);
        } finally {
            setIsSending(false);
        }
    };
    
    const formatDate = (date) => {
        const now = new Date();
        const messageDate = new Date(date);
        
        if (messageDate.toDateString() === now.toDateString()) {
            return messageDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        } else {
            return messageDate.toLocaleDateString([], {month: 'short', day: 'numeric'}) + 
                   ' ' + messageDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        }
    };
    
    // Add this useEffect to fetch analytics data
    useEffect(() => {
        const fetchAnalyticsData = async () => {
            setAnalyticsLoading(true);
            
            try {
                // Calculate the date range
                const today = new Date();
                let startDate = new Date();
                
                if (dateRange === 'week') {
                    startDate.setDate(today.getDate() - 7);
                } else if (dateRange === 'month') {
                    startDate.setDate(today.getDate() - 30);
                } else if (dateRange === 'year') {
                    startDate.setDate(today.getDate() - 365);
                }
                
                const startDateString = startDate.toISOString().split('T')[0];
                const endDateString = today.toISOString().split('T')[0];
                
                const analyticsRef = collection(db, 'analytics');
                const q = query(
                    analyticsRef,
                    where('date', '>=', startDateString),
                    where('date', '<=', endDateString),
                    orderBy('date', 'asc')
                );
                
                const querySnapshot = await getDocs(q);
                
                // Process the data
                const data = querySnapshot.docs.map(doc => {
                    const docData = doc.data();
                    
                    return {
                        id: doc.id,
                        ...docData,
                        totalUsers: Number(docData.totalUsers || 0),
                        proUsers: Number(docData.proUsers || 0),
                        conversionRate: Number(docData.conversionRate || 0),
                        subscriptionStatuses: {
                            active: Number(docData.subscriptionStatuses?.active || 0),
                            pending_cancellation: Number(docData.subscriptionStatuses?.pending_cancellation || 0),
                            canceled: Number(docData.subscriptionStatuses?.canceled || 0)
                        },
                    };
                });
                
                setAnalyticsData(data);
            } catch (error) {
                console.error('Error fetching analytics data:', error);
            } finally {
                setAnalyticsLoading(false);
            }
        };
        
        // Only fetch analytics data when the analytics tab is active
        if (activeTab === 'analytics') {
            fetchAnalyticsData();
        }
    }, [activeTab, dateRange]);
    
    const handleBackfillData = async () => {
        setBackfillStatus({ loading: true, message: 'Backfilling analytics data...' });
        
        try {
            const functions = getFunctions();
            const backfillAnalytics = httpsCallable(functions, 'backfillAnalyticsData');
            const result = await backfillAnalytics({ days: 30 });
            
            setBackfillStatus({ 
                loading: false, 
                message: `Success! ${result.data.message}` 
            });
            
            if (activeTab === 'analytics') {
                setDateRange(dateRange); // Trigger analytics refresh
            }
        } catch (error) {
            console.error('Error backfilling data:', error);
            setBackfillStatus({ 
                loading: false, 
                message: `Error: ${error.message}` 
            });
        }
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <h2>Admin Dashboard</h2>
                <div className="admin-content loading">
                    <p>Loading chats...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="admin-dashboard">

            {/* Desktop sidebar — hidden on mobile via CSS */}
            <div className="admin-sidebar">
                <div className="admin-sidebar-title">Admin Dashboard</div>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`admin-sidebar-button ${activeTab === tab.id ? 'admin-sidebar-button-active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Main content — contains mobile header, dropdown, and tab content */}
            <div className="admin-main">
            <h2>Admin Dashboard</h2>

            <div className="admin-tab-dropdown">
                <div className="dropdown-container">
                    <button 
                        className="dropdown-button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        <span>{getCurrentTabLabel()}</span>
                        <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} />
                    </button>
                    
                    {isDropdownOpen && (
                        <div 
                            className="admin-modal-backdrop"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            <div 
                                className="admin-modal-content"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="admin-modal-header">
                                    <div>
                                        Select Admin Tab
                                    </div>
                                    <button 
                                        className="admin-modal-close-btn"
                                        onClick={() => setIsDropdownOpen(false)}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="admin-modal-body">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            className={`admin-modal-button ${activeTab === tab.id ? 'admin-modal-button-active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleTabChange(tab.id);
                                            }}
                                        >
                                            {tab.label} {activeTab === tab.id ? '(Current)' : ''}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {activeTab === 'analytics' ? (
                <AnalyticsTab 
                    analyticsData={analyticsData}
                    analyticsLoading={analyticsLoading}
                    handleBackfillData={handleBackfillData}
                    backfillStatus={backfillStatus}
                />
            ) : activeTab === 'chats' ? (
                <ChatsTab
                    chats={chats}
                    selectedChat={selectedChat}
                    messages={messages}
                    newMessage={newMessage}
                    isSending={isSending}
                    messagesEndRef={messagesEndRef}
                    chatMessagesRef={chatMessagesRef}
                    handleChatSelection={handleChatSelection}
                    handleCloseChat={handleCloseChat}
                    handleSendMessage={handleSendMessage}
                    setNewMessage={setNewMessage}
                    formatDate={formatDate}
                />
            ) : activeTab === 'users' ? (
                <UsersTab
                    proUsers={proUsers}
                    selectedChat={selectedChat}
                    messages={messages}
                    newMessage={newMessage}
                    isSending={isSending}
                    messagesEndRef={messagesEndRef}
                    chatMessagesRef={chatMessagesRef}
                    handleInitiateChat={handleInitiateChat}
                    handleCloseChat={handleCloseChat}
                    handleSendMessage={handleSendMessage}
                    setNewMessage={setNewMessage}
                    formatDate={formatDate}
                />
            ) : activeTab === 'vendor_builds' ? (
                <VendorCustomBuilds />
            ) : activeTab === 'police' ? (
                <PoliceTab />
            ) : activeTab === 'videos' ? (
                <VideoTab />
            ) : activeTab === 'user_lookup' ? (
                <UserLookupTab />
            ) : (
                <NewUsersTab
                    usersWithoutChats={usersWithoutChats}
                    selectedChat={selectedChat}
                    messages={messages}
                    newMessage={newMessage}
                    isSending={isSending}
                    messagesEndRef={messagesEndRef}
                    chatMessagesRef={chatMessagesRef}
                    handleInitiateChat={handleInitiateChat}
                    handleCloseChat={handleCloseChat}
                    handleSendMessage={handleSendMessage}
                    setNewMessage={setNewMessage}
                    formatDate={formatDate}
                />
            )}
            </div> {/* end admin-main */}
        </div>
    );
};

export default AdminDashboard;